import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtEuros, StatusBadge, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DuplicateReportButton from "../components/DuplicateReportButton";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  ArrowLeft, Paperclip, Download, CheckCircle2, XCircle, Clock, MessageSquareWarning,
  HandCoins,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

const ABANDONABLE_STATUSES = [
  "DRAFT", "SUBMITTED", "PENDING_APPROVAL", "APPROVED", "PARTIALLY_APPROVED", "REJECTED",
  "CONTESTED", "BISHOP_ARBITRATION", "ARBITRATION_APPROVED", "ARBITRATION_PARTIAL",
  "ARBITRATION_REJECTED", "ACCOUNTING_PROCESSING", "ACCOUNTED",
];

export default function ReportDetail() {
  const { reportId } = useParams();
  const { user, hasRole } = useAuth();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [selectedContested, setSelectedContested] = useState([]);
  const [contestReason, setContestReason] = useState("");
  const [contestFiles, setContestFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/expense-reports/${reportId}`)
      .then(({ data }) => setReport(data.report))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const openAttachment = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canContest = report && ["PARTIALLY_APPROVED", "REJECTED"].includes(report.status) && report.is_owner;
  const rejectedExpenses = (report?.expenses || []).filter((e) => e.status === "rejected");

  const toggleContest = (id) => {
    setSelectedContested((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submitContest = async () => {
    if (selectedContested.length === 0) return toast.error("Sélectionnez au moins une dépense contestée.");
    if (contestReason.trim().length < 5) return toast.error("Expliquez votre désaccord (au moins 5 caractères).");
    setSending(true);
    try {
      for (const expId of selectedContested) {
        const file = contestFiles[expId];
        if (file) {
          const form = new FormData();
          form.append("file", file);
          await api.post(`/expenses/${expId}/attachments`, form);
        }
      }
      await api.post(`/expense-reports/${reportId}/contest`, {
        expense_ids: selectedContested, reason: contestReason.trim(),
      });
      toast.success("Votre contestation a été transmise à l'Évêque.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const submitAbandon = async () => {
    setSending(true);
    try {
      await api.post(`/expense-reports/${reportId}/abandon`);
      toast.success("Note abandonnée : son montant sera transféré au denier. Merci pour votre générosité !");
      setAbandonOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="text-lg text-slate-600">Chargement…</p>;
  if (error) return (
    <div className="card-base" data-testid="report-error">
      <p className="text-[#b91c1c] font-semibold text-lg">{error}</p>
      <Link to="/accueil" className="btn-secondary-lg mt-4">Retour à l'accueil</Link>
    </div>
  );
  if (!report) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-enter" data-testid="report-detail">
      <div>
        <Link to={report.is_owner ? "/accueil" : hasRole("admin", "super_admin") ? "/admin" : hasRole("comptable") ? "/comptabilite" : hasRole("eveque") ? "/arbitrage" : "/validation"} className="text-[#0369a1] font-semibold inline-flex items-center gap-2 mb-3" data-testid="detail-back-link">
          <ArrowLeft className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Retour
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>{report.number}</h1>
            <p className="text-lg text-slate-600 mt-1">Période {report.period} — {report.requester_name}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {report.is_owner && <DuplicateReportButton report={report} big />}
            <StatusBadge status={report.status} label={report.status_label} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="card-base !p-4 text-center">
          <p className="text-sm font-semibold text-slate-500">Demandé</p>
          <p className="text-xl font-bold text-slate-900" data-testid="total-requested">{fmtEuros(report.total_requested_cents)}</p>
        </div>
        <div className="card-base !p-4 text-center">
          <p className="text-sm font-semibold text-slate-500">Remboursé</p>
          <p className="text-xl font-bold text-[#15803d]" data-testid="total-approved">{fmtEuros(report.total_approved_cents)}</p>
        </div>
        <div className="card-base !p-4 text-center">
          <p className="text-sm font-semibold text-slate-500">Refusé</p>
          <p className="text-xl font-bold text-[#b91c1c]" data-testid="total-rejected">{fmtEuros(report.total_rejected_cents)}</p>
        </div>
      </div>
      {(report.total_donation_cents || 0) > 0 && (
        <div className="card-base !p-4 border-2 !border-rose-300 !bg-rose-50 flex items-center justify-between" data-testid="total-donation">
          <p className="font-semibold text-rose-800 flex items-center gap-2">
            <HandCoins className="h-5 w-5" aria-hidden="true" /> Transfert au denier (dépenses abandonnées)
          </p>
          <p className="text-xl font-bold text-rose-700">{fmtEuros(report.total_donation_cents)}</p>
        </div>
      )}

      <section className="card-base space-y-4" data-testid="expenses-section">
        <h2 className="text-2xl font-semibold text-slate-900">Dépenses</h2>
        {(report.expenses || []).map((e) => (
          <div key={e.id} className="border-2 border-slate-200 rounded-xl p-4 space-y-2" data-testid={`expense-row-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-bold text-lg text-slate-900">{e.category_label}</p>
                <p className="text-slate-600">{e.date} — {e.purpose}</p>
                {e.distance_km ? <p className="text-slate-500 text-sm">{e.distance_km} km</p> : null}
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold text-xl text-slate-900">{fmtEuros(e.amount_cents)}</p>
                {e.is_donation && (
                  <p className="inline-flex items-center gap-1 text-rose-700 font-semibold" data-testid={`expense-donation-badge-${e.id}`}>
                    <HandCoins className="h-4 w-4" aria-hidden="true" /> Transfert au denier
                  </p>
                )}
                {e.status === "approved" && <span className="inline-flex items-center gap-1 text-[#15803d] font-semibold"><CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Acceptée</span>}
                {e.status === "rejected" && <span className="inline-flex items-center gap-1 text-[#b91c1c] font-semibold"><XCircle className="h-4 w-4" aria-hidden="true" /> Refusée</span>}
                {e.status === "pending" && <span className="inline-flex items-center gap-1 text-slate-500 font-semibold"><Clock className="h-4 w-4" aria-hidden="true" /> En attente</span>}
              </div>
            </div>
            {e.decision_reason && e.status === "rejected" && (
              <p className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 font-medium">
                Motif du refus : {e.decision_reason}
              </p>
            )}
            {(e.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {e.attachments.map((a) => (
                  <button key={a.id} onClick={() => openAttachment(a)}
                    className="inline-flex items-center gap-2 px-3 h-10 rounded-lg border-2 border-slate-300 text-[#0369a1] font-semibold hover:bg-sky-50"
                    data-testid={`attachment-view-${a.id}`}>
                    <Paperclip className="h-4 w-4" aria-hidden="true" /> {a.filename}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      <section className="card-base" data-testid="timeline-section">
        <h2 className="text-2xl font-semibold text-slate-900 mb-4">Historique</h2>
        <ol className="space-y-3">
          {(report.history || []).map((h, i) => (
            <li key={i} className="flex gap-3 items-start">
              <span className="mt-2 h-3 w-3 rounded-full bg-[#0369a1] shrink-0" aria-hidden="true" />
              <div>
                <p className="font-semibold text-slate-800">{h.detail}</p>
                <p className="text-sm text-slate-500">{new Date(h.at).toLocaleString("fr-FR")}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {(report.decisions || []).some((d) => d.phase === "arbitration") && (
        <section className="card-base" data-testid="arbitration-section">
          <h2 className="text-2xl font-semibold text-slate-900 mb-4">Décision d'arbitrage</h2>
          {report.decisions.filter((d) => d.phase === "arbitration").slice(0, 1).map((d) => (
            <div key={d.id} className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <p className="font-semibold text-slate-800">Par {d.actor_name} — {new Date(d.created_at).toLocaleString("fr-FR")}</p>
              <p className="text-slate-700 mt-1 italic">« {d.reason} »</p>
            </div>
          ))}
        </section>
      )}

      {canContest && rejectedExpenses.length > 0 && (
        <section className="card-base border-2 !border-orange-300 space-y-5" data-testid="contest-section">
          <div className="flex items-center gap-3">
            <MessageSquareWarning className="h-8 w-8 text-[#c2410c]" aria-hidden="true" strokeWidth={2.5} />
            <h2 className="text-2xl font-semibold text-slate-900">Contester cette décision</h2>
          </div>
          <p className="text-slate-600 text-lg">
            Si vous n'êtes pas d'accord avec un refus, cochez les dépenses concernées et expliquez votre point de vue.
            Votre demande sera transmise à l'Évêque, dont la décision sera finale.
          </p>
          <div className="space-y-3">
            {rejectedExpenses.map((e) => (
              <label key={e.id} className="flex items-start gap-3 border-2 border-slate-200 rounded-xl p-4 cursor-pointer hover:border-orange-300"
                data-testid={`contest-expense-${e.id}`}>
                <Checkbox checked={selectedContested.includes(e.id)} onCheckedChange={() => toggleContest(e.id)}
                  className="mt-1 h-6 w-6" data-testid={`contest-checkbox-${e.id}`} />
                <span className="flex-1">
                  <span className="font-bold text-slate-900 block">{e.category_label} — {fmtEuros(e.amount_cents)}</span>
                  <span className="text-slate-600">{e.date} — {e.purpose}</span>
                </span>
              </label>
            ))}
          </div>
          {selectedContested.map((expId) => (
            <div key={expId}>
              <Label className="label-lg">Nouveau justificatif (facultatif) pour cette dépense</Label>
              <input type="file" accept="image/*,.pdf,.heic" className="block w-full text-lg"
                onChange={(ev) => setContestFiles((p) => ({ ...p, [expId]: ev.target.files?.[0] }))}
                data-testid={`contest-file-${expId}`} />
            </div>
          ))}
          <div>
            <Label htmlFor="contest-reason" className="label-lg">Expliquez votre désaccord (obligatoire)</Label>
            <Textarea id="contest-reason" value={contestReason} onChange={(e) => setContestReason(e.target.value)}
              className="text-lg border-2 border-slate-300 rounded-xl px-4 py-3 min-h-28 w-full"
              placeholder="Ex : ce repas était lié à un déplacement pastoral, voici la convocation…"
              data-testid="contest-reason-input" />
          </div>
          <button onClick={submitContest} disabled={sending} className="btn-danger-lg w-full !bg-[#c2410c] hover:!bg-orange-800" data-testid="contest-submit-button">
            {sending ? "Envoi en cours…" : "Envoyer ma contestation à l'Évêque"}
          </button>
        </section>
      )}

      {report.is_owner && ABANDONABLE_STATUSES.includes(report.status) && (
        <section className="card-base border-2 !border-rose-200 space-y-4" data-testid="abandon-section">
          <div className="flex items-center gap-3">
            <HandCoins className="h-8 w-8 text-rose-600" aria-hidden="true" strokeWidth={2.5} />
            <h2 className="text-2xl font-semibold text-slate-900">Abandonner cette note</h2>
            <button onClick={() => setHelpOpen(true)} aria-label="Qu'est-ce que l'abandon ?"
              className="h-10 w-10 rounded-full border-2 border-slate-400 text-slate-600 font-bold text-xl flex items-center justify-center hover:bg-slate-100 transition-colors"
              data-testid="abandon-help-button">?</button>
          </div>
          <p className="text-slate-600 text-lg">
            Vous pouvez renoncer au remboursement de cette note : son montant sera alors <strong>transféré au denier</strong>.
          </p>
          <button onClick={() => setAbandonOpen(true)} className="btn-danger-lg !bg-rose-700 hover:!bg-rose-800" data-testid="abandon-button">
            J'abandonne cette note (transfert au denier)
          </button>
        </section>
      )}

      {report.accounting && (
        <section className="card-base" data-testid="accounting-info">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Informations comptables</h2>
          <p className="text-slate-700">Compte : <strong>{report.accounting.account}</strong>
            {report.accounting.analytic ? <> — Analytique : <strong>{report.accounting.analytic}</strong></> : null}
            {report.accounting.cost_center ? <> — Centre de coût : <strong>{report.accounting.cost_center}</strong></> : null}
          </p>
        </section>
      )}
      {report.payment && (
        <section className="card-base !bg-green-50 !border-green-300" data-testid="payment-info">
          <h2 className="text-xl font-semibold text-green-900 mb-2 flex items-center gap-2">
            <Download className="h-5 w-5" aria-hidden="true" /> Paiement effectué
          </h2>
          <p className="text-green-800">Référence : <strong>{report.payment.reference}</strong> — {new Date(report.payment.paid_at).toLocaleString("fr-FR")}</p>
        </section>
      )}

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="abandon-help-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Qu'est-ce que l'abandon ?</DialogTitle>
            <DialogDescription className="text-base">Explication de l'abandon d'une note de frais (transfert au denier).</DialogDescription>
          </DialogHeader>
          <div className="text-lg text-slate-700 space-y-3 py-2">
            <p>Abandonner une note de frais signifie que vous <strong>renoncez à son remboursement</strong>.</p>
            <p>Le montant correspondant sera <strong>transféré au denier</strong>. La comptabilité
              est automatiquement informée : ce transfert est suivi <strong>séparément des notes de frais</strong>,
              pour l'établissement éventuel d'un <strong>reçu fiscal</strong> (traité en dehors de cette
              application, au niveau du denier).</p>
            <p>Cette action est <strong>définitive</strong> : la note ne pourra plus être remboursée ni modifiée.</p>
          </div>
          <button onClick={() => setHelpOpen(false)} className="btn-primary-lg w-full" data-testid="abandon-help-close">J'ai compris</button>
        </DialogContent>
      </Dialog>

      <Dialog open={abandonOpen} onOpenChange={setAbandonOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="abandon-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Confirmer l'abandon</DialogTitle>
            <DialogDescription className="text-base">Confirmation définitive du transfert de la note au denier.</DialogDescription>
          </DialogHeader>
          <div className="text-lg text-slate-700 space-y-3 py-2">
            <p>Vous allez abandonner la note <strong>{report.number}</strong>
              {report.status === "DRAFT" ? " (brouillon, pas encore envoyée)" : ""} — son montant sera transféré au denier
              et elle ne sera <strong>plus jamais remboursée</strong>.</p>
            {report.status === "DRAFT" && (report.total_requested_cents || 0) === 0 && (
              <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 text-base">
                Attention : ce brouillon ne contient aucune dépense (montant 0 €) — le transfert au denier sera de 0 €.
              </p>
            )}
            <p>Cette action est définitive.</p>
          </div>
          <div className="flex gap-3 flex-col sm:flex-row">
            <button onClick={() => setAbandonOpen(false)} className="btn-secondary-lg flex-1" data-testid="abandon-cancel-button">Annuler</button>
            <button onClick={submitAbandon} disabled={sending} className="btn-danger-lg flex-1 !bg-rose-700 hover:!bg-rose-800" data-testid="abandon-confirm-button">
              {sending ? "En cours…" : "Confirmer l'abandon"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
